import { isAbortError } from '@web-platform/shared-universal-utils-frontend/src/helpers/abortErrorHelpers';
import { logError } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import { queryRequestSent, queryResponseReceived } from '../../actions/network';

import { getRequestQueryClient } from './getRequestQueryClient';
import { reduceQueryStateToNextQueries } from './queryStateFilters';

import type {
  Action,
  GenericErrorPayloadResponse,
  PayloadResponse,
  TimeoutErrorPayloadResponse,
} from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type { QueryState } from '@web-platform/shared-universal-types/src/redux/state/network/queries';
import type { Dispatch } from 'redux';

function getErrorPayload(err: any): PayloadResponse {
  const isAbortTimeout = isAbortError(err);
  let errorPayload: PayloadResponse;
  if (isAbortTimeout) {
    const timeoutError: TimeoutErrorPayloadResponse = {
      headers: null,
      error: true,
      body: null,
      status: 504,
    };
    errorPayload = timeoutError;
  } else {
    const genericError: GenericErrorPayloadResponse = {
      headers: null,
      error: true,
      body: null,
      status: null,
    };
    errorPayload = genericError;
  }

  return errorPayload;
}

export function handleImmediateQueries(
  state: QueryState,
  dispatch: Dispatch<Action>,
): Promise<void[]> {
  return Promise.all(
    Object.keys(state).map((itemId) => {
      const item = state[itemId];

      // It's critical that QUERY_REQUEST_SENT is dispatched successfully before the request begins, else there would be a risk of runaway requests
      dispatch(queryRequestSent(item.type, item.variables));

      const request = getRequestQueryClient(item);
      return request()
        .then(
          (response) => {
            dispatch(queryResponseReceived(item.type, item.variables, response));
          },
          (err) => {
            const errorPayload = getErrorPayload(err);
            dispatch(queryResponseReceived(item.type, item.variables, errorPayload));
          },
        )
        .catch((err) => {
          logError({
            component: 'actUponQueryState',
            error: err,
            message: 'Error with actUponQueryState',
            stack: err && err.stack,
          });
        });
    }),
  );
}

function scheduleFutureQueries(
  minNextRequestAt: number,
  dispatch: Dispatch<Action>,
  getState: () => QueryState,
) {
  const timeToNextQuery = minNextRequestAt - Date.now();
  setTimeout(() => actUponQueryState(getState, dispatch), timeToNextQuery);
}

export function actUponQueryState(
  getState: () => QueryState,
  dispatch: Dispatch<Action>,
): Promise<void[]> {
  const { immediateQueryState, minNextRequestAt } = reduceQueryStateToNextQueries(
    getState(),
    Date.now(),
  );

  if (minNextRequestAt) {
    scheduleFutureQueries(minNextRequestAt, dispatch, getState);
  }

  return handleImmediateQueries(immediateQueryState, dispatch);
}
